import { useMemo, useState } from 'react';
import { createRoute, redirect } from '@tanstack/react-router';
import { Route as appRoute } from '../app.js';
import { useAuthStore } from '../../lib/auth.js';
import { usePricingCourses, type PricingCourseRow } from '../../hooks/usePricing.js';
import {
  activeScholarship,
  isNegativeNet,
  matchesCourseQuery,
  scholarshipLabel,
} from '../../lib/pricing.js';
import { centsToAudDisplay } from '../../lib/money.js';
import { formatDate } from '../../lib/format.js';
import { Input } from '../../components/ui/Input.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: '/pricing',
  // Consultants only — admins and super_admins have the editable console at
  // /app/admin/pricing. The server's view_pricing capability stays the real
  // gate: a consultant whose view_pricing was revoked by override gets a 403,
  // surfaced through the error state below.
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'consultant') throw redirect({ to: '/app' });
  },
  component: ConsultantPricingPage,
});

/**
 * Read-only pricing list for consultants (Brief C1): every visible course with
 * the agency fee, the current scholarship and the SERVER-computed net fee, so
 * a consultant can quote without asking an admin. No edit controls, no history,
 * no admin links — and the net is never recomputed in the browser.
 */
export function ConsultantPricingPage() {
  const { data, isLoading, error, refetch } = usePricingCourses();
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      (data?.items ?? [])
        .filter((c) => matchesCourseQuery(c, search))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [data, search],
  );

  return (
    <div className="max-w-4xl">
      <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted font-semibold">
        Pricing
      </div>
      <h1 className="font-serif text-4xl tracking-tighter2 mb-2">Course pricing</h1>
      <p className="text-muted text-sm mb-6">Prices are managed by the pricing team. Read-only.</p>

      <div className="mb-4 max-w-md">
        <Input
          placeholder="Search course or college…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {data?.asOf && (
        <p className="mb-4 text-xs text-muted">Prices current as of {formatDate(data.asOf)}.</p>
      )}

      {isLoading && <p className="px-1 py-6 text-sm text-muted">Loading courses…</p>}

      {error && (
        <div className="rounded-lg border border-danger/30 bg-danger-bg px-4 py-3 text-sm text-danger">
          Couldn't load courses: {(error as Error).message}{' '}
          <button className="underline" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <p className="px-1 py-6 text-sm text-muted">No courses found</p>
      )}

      {!isLoading && !error && filtered.length > 0 && (
        <div className="surface p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-cream/60 border-b border-line">
              <tr className="text-left text-muted text-xs uppercase tracking-wider">
                <th className="px-5 py-3 font-medium">Course</th>
                <th className="px-5 py-3 font-medium text-right">Fee</th>
                <th className="px-5 py-3 font-medium text-right">Scholarship</th>
                <th className="px-5 py-3 font-medium text-right">Net fee</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {filtered.map((c) => (
                <PricingRow key={c.courseId} course={c} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PricingRow({ course: c }: { course: PricingCourseRow }) {
  // The API always sends both fields; `?? null` covers older cached shapes.
  const net = c.netFeeCents ?? null;
  // null / kind 'none' / malformed all mean: no scholarship in force → "—".
  const sch = activeScholarship(c.scholarship);

  return (
    <tr className="hover:bg-cream/30 transition-colors align-top">
      <td className="px-5 py-3 min-w-[16rem]">
        <span className="block text-sm text-ink">{c.name}</span>
        <span className="block text-xs text-muted">{c.providerName}</span>
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {c.agencyFee ? (
          <span className="text-sm font-medium text-navy">
            {centsToAudDisplay(c.agencyFee.tuitionCents)}
          </span>
        ) : (
          <span className="badge bg-cream-deep/60 text-muted">No fee set</span>
        )}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap text-sm text-ink">
        {sch ? scholarshipLabel(sch) : '—'}
      </td>
      <td className="px-5 py-3 text-right whitespace-nowrap">
        {net === null ? (
          <span className="text-sm text-muted">—</span>
        ) : isNegativeNet(net) ? (
          // A scholarship now exceeds the fee — shown honestly, never clamped.
          <span
            data-testid="negative-net"
            className="inline-block rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger"
          >
            {centsToAudDisplay(net)}
          </span>
        ) : (
          <span className="text-sm font-medium text-navy">{centsToAudDisplay(net)}</span>
        )}
      </td>
    </tr>
  );
}
